import {
  AmazonOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Dropdown, Layout, Menu, Segmented, Space, Tag, Typography, message } from "antd";
import type { MenuProps } from "antd";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LoadingState } from "../components/FeedbackStates";
import { MetzLogo } from "../components/MetzLogo";
import { TableDisplayControl } from "../components/TableDisplayControl";
import { useTableDisplayPreferences } from "../components/tableDisplayPreferences";
import type { NeoEnvironmentDTO } from "../api/neo";
import { getNeoEnvironment } from "../api/neo";
import {
  changeLocale,
  type SupportedLocale,
} from "../i18n";
import { findShellRoute, minimumRoleForRoute, navigationGroups } from "../routes/routeManifest";
import { OnlinePreviewContext } from "../context/OnlinePreviewContext";
import { roleAllows } from "../api/auth";
import { useAuthentication } from "../context/AuthContext";
import { frontendEnvironment } from "../api/env";

const { Header, Content, Sider } = Layout;

const groupIcons = {
  Global: <GlobalOutlined />,
  Amazon: <AmazonOutlined />,
  Joybuy: <ShopOutlined />,
  System: <SettingOutlined />,
};

const groupNavKeys: Record<string, string> = {
  Global: "navigation.group.global",
  Amazon: "navigation.group.amazon",
  Joybuy: "navigation.group.joybuy",
  System: "navigation.group.system",
};

// Route display labels are resolved through stable translation keys. The
// route `path` remains the identity and navigation key; only the rendered
// label is localized.
const routeNavKeys: Record<string, string> = {
  "/global/action-center": "navigation.actionCenter",
  "/global/sales": "navigation.globalSales",
  "/system/sell-out": "navigation.sellOut",
  "/global/inventory": "navigation.planningInventory",
  "/global/cost": "navigation.globalCost",
  "/global/profitability": "navigation.globalProfitability",
  "/finance/invoice-reconciliation": "navigation.invoiceReconciliation",
  "/amazon/sales": "navigation.amazonSales",
  "/amazon/inventory": "navigation.amazonInventory",
  "/amazon/promotion": "navigation.amazonPromotion",
  "/amazon/rebate-settlement": "navigation.amazonRebateSettlement",
  "/amazon/operations": "navigation.amazonOperation",
  "/amazon/data-upload": "navigation.amazonDataUpload",
  "/joybuy/sales": "navigation.joybuySales",
  "/joybuy/inventory": "navigation.joybuyInventory",
  "/joybuy/order-planning": "navigation.joybuyOrderPlanning",
  "/joybuy/operation": "navigation.joybuyOperation",
  "/joybuy/traffic": "navigation.joybuyTraffic",
  "/joybuy/data-upload": "navigation.joybuyDataUpload",
  "/system/data-import-center": "navigation.dataImportCenter",
  "/system/data-sources": "navigation.dataSourcesIdentity",
  "/system/master-data": "navigation.masterData",
  "/system/users-access": "Users & Access",
  "/diagnostics/backend-connectivity": "navigation.backendConnectivity",
};

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [tableDisplay, setTableDisplay] = useTableDisplayPreferences();
  const [environment, setEnvironment] = useState<NeoEnvironmentDTO | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [api, contextHolder] = message.useMessage();
  const authentication = useAuthentication();
  const currentRoute = findShellRoute(location.pathname);
  const sourceMaintenance = location.pathname === "/system/data-sources";
  useEffect(() => {
    const controller = new AbortController();
    void getNeoEnvironment(controller.signal).then(setEnvironment).catch(() => setEnvironment(null));
    return () => controller.abort();
  }, []);
  const onlinePreview = environment?.runtime_environment?.toLowerCase() === "online_preview";
  const staging = environment?.runtime_environment?.toLowerCase() === "staging";
  const menuItems = useMemo<MenuProps["items"]>(
    () =>
      navigationGroups.map((group) => ({
        key: group.label,
        icon: groupIcons[group.label],
        label: t(groupNavKeys[group.label] ?? "navigation.group.global", group.label),
        children: group.routes.filter((route) =>
          (!onlinePreview || !["/system/data-import-center", "/system/master-data"].includes(route.path)) &&
          authentication.user !== null && roleAllows(authentication.user.role, minimumRoleForRoute(route)),
        ).map((route) => ({
          key: route.path,
          icon:
            route.kind === "forecast" ? (
              <BarChartOutlined />
            ) : route.kind === "diagnostic" ? (
              <DatabaseOutlined />
            ) : undefined,
          label: t(routeNavKeys[route.path] ?? "navigation.globalSales", route.label),
        })),
      })),
    // The translate function is stable across re-renders; the menu is rebuilt
    // when the language or preview mode changes so labels stay in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, onlinePreview, authentication.user],
  );

  const shareCurrentView = async () => {
    try { await navigator.clipboard.writeText(window.location.href); api.success(t("common.copiedViewLink", "Current view link copied, including filters in the URL.")); }
    catch { api.error(t("common.copyViewLinkFailed", "Could not copy the current view link.")); }
  };

  const environmentTag = environment
    ? `${onlinePreview ? t("common.onlinePreview", "ONLINE PREVIEW") : staging ? "STAGING" : t("common.neoUat", "NEO UAT")} · ${environment.database_name} · ${environment.alembic_revision.slice(0, 8)}`
    : t("common.environmentUnverified", "ENVIRONMENT UNVERIFIED");
  const environmentTitle = environment
    ? `${environment.database_name} · ${environment.alembic_revision} · ${environment.source_baseline_timestamp ?? t("common.noSourceTimestamp", "No source timestamp")}`
    : t("common.environmentIdentityUnavailable", "Environment identity unavailable");
  const modeTag = onlinePreview
    ? t("common.onlinePreviewReadOnly", "ONLINE PREVIEW · READ ONLY")
    : staging
      ? `${authentication.user?.role ?? "UNKNOWN"} · GOVERNED`
    : sourceMaintenance
      ? t("common.sourceUatMaintenance", "SOURCE UAT · MAINTENANCE")
      : t("common.uatReadOnly", "UAT · READ ONLY");
  const groupLabel = currentRoute
    ? t(groupNavKeys[currentRoute.group] ?? "navigation.group.global", currentRoute.group)
    : "MBIP";
  const pageLabel = currentRoute
    ? t(routeNavKeys[currentRoute.path] ?? "navigation.globalSales", currentRoute.label)
    : "MBIP";
  const userMenuItems: MenuProps["items"] = authentication.user === null ? [] : [
    { key: "identity", disabled: true, label: <div className="mbip-user-menu__identity"><strong>{authentication.user.username}</strong><span>{authentication.user.role}</span></div> },
    { type: "divider" },
    { key: "change-password", icon: <KeyOutlined />, label: "Change password", onClick: () => navigate("/change-password") },
    { key: "logout", icon: <LogoutOutlined />, label: "Sign out", onClick: () => void authentication.signOut() },
  ];

  return (
    <Layout
      className="mbip-shell"
      data-table-column-width={tableDisplay.columnWidth}
      data-table-row-height={tableDisplay.rowHeight}
    >
      {contextHolder}
      <a className="skip-link" href="#main-content">{t("common.skipToContent", "Skip to main content")}</a>
      <Sider
        className="mbip-sider"
        width={232}
        collapsedWidth={64}
        collapsed={collapsed}
        trigger={null}
      >
        <button
          type="button"
          className={`mbip-brand${collapsed ? " mbip-brand--collapsed" : ""}`}
          aria-label={t("common.homeAria", "MBIP home")}
          onClick={() => navigate("/")}
        >
          <MetzLogo compact={collapsed} />
          {collapsed ? null : (
            <span className="mbip-brand__copy">
              <small>{t("common.brandTagline", "Business Intelligence")}</small>
            </span>
          )}
        </button>
        <nav aria-label={t("common.primaryNavigation", "Primary navigation")}>
          <Menu
            theme="light"
            mode="inline"
            items={menuItems}
            selectedKeys={[location.pathname]}
            defaultOpenKeys={[currentRoute?.group ?? "Global"]}
            onClick={({ key }) => navigate(key)}
          />
        </nav>
      </Sider>

      <Layout>
        <Header className="mbip-header">
          <Button
            type="text"
            aria-label={collapsed ? t("common.expandNavigation", "Expand navigation") : t("common.collapseNavigation", "Collapse navigation")}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((value) => !value)}
          />
          <div className="mbip-header__context">
            <Typography.Text type="secondary">{groupLabel}</Typography.Text>
            <span className="mbip-header__context-divider" aria-hidden="true">/</span>
            <Typography.Text strong>{pageLabel}</Typography.Text>
          </div>
          <Segmented
            className="mbip-header__lang"
            aria-label={t("common.language", "Language")}
            size="small"
            value={i18n.language}
            onChange={(value) => changeLocale(value as SupportedLocale)}
            options={[
              { value: "en", label: t("common.english", "English") },
              { value: "zh-CN", label: t("common.chinese", "中文") },
            ]}
          />
          <TableDisplayControl value={tableDisplay} onChange={setTableDisplay} />
          <Tag color={environment ? undefined : "warning"} className="mbip-header__tag mbip-header__environment" title={environmentTitle}>
            <span className="mbip-header__environment-name">{environmentTag.split(" · ")[0]}</span>
            <span className="mbip-header__environment-detail">{environmentTag.includes(" · ") ? ` · ${environmentTag.split(" · ").slice(1).join(" · ")}` : ""}</span>
          </Tag>
          {onlinePreview ? <Button size="small" onClick={() => void shareCurrentView()}>{t("common.shareCurrentView", "Share current view")}</Button> : authentication.user !== null && roleAllows(authentication.user.role, "ADMIN") ? <Button size="small" icon={<DatabaseOutlined />} onClick={() => navigate("/system/master-data")}>{t("common.masterData", "Master Data")}</Button> : null}
          {frontendEnvironment.authRequired && authentication.user !== null ? <Dropdown menu={{ items: userMenuItems }} trigger={["click"]} placement="bottomRight">
            <Button size="small" className="mbip-user-menu" aria-label="Open user menu">
              <Space size={6}><Avatar size={20} icon={<UserOutlined />} /><span>{authentication.user.username}</span><Tag>{authentication.user.role}</Tag></Space>
            </Button>
          </Dropdown> : null}
          <Tag variant="filled" color={sourceMaintenance ? "green" : "blue"} className="mbip-header__tag mbip-header__mode-tag">
            {modeTag}
          </Tag>
        </Header>
        <Content className="mbip-content" id="main-content" tabIndex={-1}>
          {frontendEnvironment.demoLabel ? <div role="note" style={{ position: "fixed", right: 16, bottom: 10, zIndex: 2000, maxWidth: "calc(100vw - 32px)" }}><Tag color="gold" style={{ whiteSpace: "normal", fontSize: 13 }}>{frontendEnvironment.demoLabel}</Tag></div> : null}
          <Suspense fallback={<LoadingState title={t("common.loadingWorkspace", "Loading workspace")} />}>
            <OnlinePreviewContext.Provider value={onlinePreview}><Outlet /></OnlinePreviewContext.Provider>
          </Suspense>
        </Content>
      </Layout>
    </Layout>
  );
}
